import React, { useEffect, useState, useMemo } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import TaskSkeleton from './TaskSkeleton';
import moment from 'moment';
import Select from 'react-select';

export default function ConsultantReport() {
    const { http, user } = AuthUser();
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedConsultant, setSelectedConsultant] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Status options
    const statusOptions = [
        { value: 1, label: 'Approved' },
        { value: 0, label: 'Not Approved' },
        { value: 2, label: 'Cancelled' },
        { value: 3, label: 'Hold' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        input: (base) => ({ ...base, margin: '0', padding: '0' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchReportData();
    }, []);

    const fetchReportData = () => {
        setIsLoading(true);
        http.get(`/consultant_report?company_id=${user.company_id}`)
            .then((res) => {
                setIsLoading(false);
                if (res.data.success && Array.isArray(res.data.data)) {
                    setReportData(res.data.data);
                } else {
                    console.error("API response is not as expected:", res.data);
                    toast.error("Failed to load report data");
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching report data:", error);
                toast.error("Failed to load consultant report");
            });
    };

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            0: 'bg-warning text-dark',
            1: 'bg-success',
            2: 'bg-danger',
            3: 'bg-info'
        };
        return statusMap[status] || 'bg-secondary';
    };

    const getStatusText = (status) => {
        const statusMap = {
            0: 'Not Approved',
            1: 'Approved',
            2: 'Cancelled',
            3: 'Hold'
        };
        return statusMap[status] || 'Pending';
    };

    // Get unique consultants for filter (react-select format)
    const consultantOptions = useMemo(() => reportData.map(c => ({
        value: c.user_id,
        label: `${c.user?.name || ''} ${c.user?.last_name || ''}`.trim()
    })), [reportData]);

    // Get unique classes for filter (react-select format)
    const classOptions = useMemo(() => {
        const uniqueClasses = [...new Set(reportData.flatMap(c => c.classes.map(cl => cl.class)))].filter(Boolean);
        return uniqueClasses.map(cls => ({ value: cls, label: cls }));
    }, [reportData]);

    // Flatten data for table view
    const getTableData = () => {
        let tableData = [];
        
        reportData.forEach(consultant => {
            consultant.classes.forEach(classItem => {
                classItem.students.forEach(student => {
                    tableData.push({
                        consultantId: consultant.user_id,
                        consultantName: `${consultant.user?.name || ''} ${consultant.user?.last_name || ''}`.trim(),
                        consultantEmail: consultant.user?.email,
                        className: classItem.class,
                        allocation: classItem.allocation,
                        capacity: classItem.class_allocation?.capacity,
                        openSeats: classItem.class_allocation?.open_seats,
                        student: student,
                        totalAllocation: consultant.total_allocation,
                        totalStudents: consultant.total_students
                    });
                });
            });
        });

        // Apply filters
        if (selectedConsultant) {
            tableData = tableData.filter(row => row.consultantId === selectedConsultant.value);
        }
        if (selectedClass) {
            tableData = tableData.filter(row => row.className === selectedClass.value);
        }
        if (selectedStatus) {
            tableData = tableData.filter(row => row.student.status === selectedStatus.value);
        }

        return tableData;
    };

    // Get summary data
    const getSummaryData = () => {
        let summary = reportData.map(consultant => ({
            id: consultant.user_id,
            name: `${consultant.user?.name || ''} ${consultant.user?.last_name || ''}`.trim(),
            email: consultant.user?.email,
            totalAllocation: consultant.total_allocation,
            totalStudents: consultant.total_students,
            approved: consultant.overall_status_counts?.Approval || 0,
            notApproved: consultant.overall_status_counts?.['Not Approval'] || 0,
            cancelled: consultant.overall_status_counts?.Canceled || 0,
            hold: consultant.overall_status_counts?.Pending || 0,
            classes: consultant.classes.length
        }));

        if (selectedConsultant) {
            summary = summary.filter(s => s.id === selectedConsultant.value);
        }

        return summary;
    };

    const tableData = getTableData();
    const summaryData = getSummaryData();

    const resetFilters = () => {
        setSelectedConsultant(null);
        setSelectedClass(null);
        setSelectedStatus(null);
    };

    if (isLoading) {
        return (
            <div className="container-fluid px-3 pt-2">
                <TaskSkeleton />
            </div>
        );
    }

    return (
        <>
            <Toaster />
            <div className="container-fluid px-3 pt-2">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Consultant Report</h5>
                    <button className="btn btn-primary btn-sm" onClick={fetchReportData}>
                        <i className="fas fa-sync-alt me-1"></i>Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="card mb-2 border-0 shadow-sm" style={{ background: '#f8f9fa' }}>
                    <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div style={{ width: '170px' }}>
                                <Select
                                    options={consultantOptions}
                                    value={selectedConsultant}
                                    onChange={setSelectedConsultant}
                                    placeholder="Select Consultant..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div style={{ width: '130px' }}>
                                <Select
                                    options={classOptions}
                                    value={selectedClass}
                                    onChange={setSelectedClass}
                                    placeholder="Class"
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div style={{ width: '140px' }}>
                                <Select
                                    options={statusOptions}
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    placeholder="Select Status..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={resetFilters} title="Reset Filters">
                                <i className="fa fa-undo me-1"></i>Reset
                            </button>
                        </div>
                    </div>
                </div>

                {reportData.length === 0 ? (
                    <div className="alert alert-info">No consultant data found.</div>
                ) : (
                    <>
                        {/* Summary Table */}
                        <div className="card mb-3">
                            <div className="card-header py-2 bg-primary text-white">
                                <strong><i className="fa fa-chart-bar me-2"></i>Consultant Summary</strong>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Consultant</th>
                                                <th>Email</th>
                                                <th className="text-center">Classes</th>
                                                <th className="text-center">Allocation</th>
                                                <th className="text-center">Students</th>
                                                <th className="text-center">
                                                    <span className="badge bg-success">Approved</span>
                                                </th>
                                                <th className="text-center">
                                                    <span className="badge bg-warning text-dark">Not Approved</span>
                                                </th>
                                                <th className="text-center">
                                                    <span className="badge bg-info">Hold</span>
                                                </th>
                                                <th className="text-center">
                                                    <span className="badge bg-danger">Cancelled</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summaryData.map((row, idx) => (
                                                <tr key={row.id}>
                                                    <td>{idx + 1}</td>
                                                    <td><strong>{row.name}</strong></td>
                                                    <td><small className="text-muted">{row.email}</small></td>
                                                    <td className="text-center">{row.classes}</td>
                                                    <td className="text-center"><span className="badge bg-primary">{row.totalAllocation}</span></td>
                                                    <td className="text-center"><span className="badge bg-dark">{row.totalStudents}</span></td>
                                                    <td className="text-center"><strong className="text-success">{row.approved}</strong></td>
                                                    <td className="text-center"><strong className="text-warning">{row.notApproved}</strong></td>
                                                    <td className="text-center"><strong className="text-info">{row.hold}</strong></td>
                                                    <td className="text-center"><strong className="text-danger">{row.cancelled}</strong></td>
                                                </tr>
                                            ))}
                                            {summaryData.length === 0 && (
                                                <tr><td colSpan="10" className="text-center text-muted">No data</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Student Details Table */}
                        <div className="card">
                            <div className="card-header py-2 bg-secondary text-white">
                                <strong><i className="fa fa-users me-2"></i>Student Details</strong>
                                <span className="badge bg-light text-dark ms-2">{tableData.length} records</span>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-hover table-striped mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>#</th>
                                                <th>Consultant</th>
                                                <th>Class</th>
                                                <th>Student Name</th>
                                                <th>Mobile</th>
                                                <th>Email</th>
                                                <th>Father's Name</th>
                                                <th>Status</th>
                                                <th>Type</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableData.map((row, idx) => (
                                                <tr key={`${row.student.id}-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>
                                                        <small>{row.consultantName}</small>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark">{row.className || '-'}</span>
                                                    </td>
                                                    <td><strong>{row.student.student_name}</strong></td>
                                                    <td>{row.student.mobile_number || '-'}</td>
                                                    <td><small>{row.student.email_address || '-'}</small></td>
                                                    <td>{row.student.father_name || '-'}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadgeClass(row.student.status)}`}>
                                                            {getStatusText(row.student.status)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-secondary">
                                                            {row.student.type === 0 ? 'Open Seat' : 'Consult'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {moment(row.student.created_at).format('DD MMM YY')}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                            {tableData.length === 0 && (
                                                <tr>
                                                    <td colSpan="10" className="text-center text-muted py-3">
                                                        No students found matching the filters
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
